'use client'

import { Suspense, useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { isAnalyticsAllowed } from '@/lib/consent-manager'
import { clearVisitorId } from '@/lib/analytics/visitor-id'
import { clearStoredAIReferrer } from '@/lib/analytics/ai-referrer-tracker'
import {
  enablePostHogCapture,
  disablePostHogCapture,
  capturePostHogPageview,
} from '@/lib/analytics/posthog-client'
import {
  captureFirstTouchUtm,
  clearFirstTouchUtm,
} from '@/lib/analytics/first-touch-utm'
import { isSitesControlPlanePath } from '@/lib/sites/control-plane'

/**
 * AnalyticsGate — policy-gated PostHog lifecycle (Radar product analytics).
 *
 * Mounted on ALL routes (outside ConditionalMarketingShell) so the Radar
 * product surface /platform/app is instrumented for direct magic-link entry
 * too. GA4 and Clarity keep loading via policy-gated Scripts in layout.tsx;
 * Vercel Analytics stays marketing-scoped in layout.tsx. This component
 * owns PostHog only:
 *
 * - Initializes the SDK once, when the centrally resolved effective
 *   permission allows it: an explicit grant (any region), the
 *   notice-opt-out regional default with no decision, or the
 *   consentGranted CustomEvent from the banner.
 * - Opts out immediately on consentDenied (a stored denial overrides every
 *   regional default).
 * - Sends manual $pageview events for genuine pathname/search navigation
 *   (automatic pageviews are disabled). URL-level dedupe in posthog-client
 *   makes repeated consent events and effect re-runs harmless.
 *
 * The GA4 page_view ownership contract (layout loader = initial view,
 * Ga4SpaTracking = route changes) is not involved here — PostHog has its
 * own single sender with its own dedupe.
 *
 * ONE exception to "all routes": the Radar Sites control plane is a
 * no-capture boundary. `/sites/invitations/<token>` puts a one-time bearer
 * credential in the PATHNAME, and PostHog's `$`-prefixed URL fields are
 * deliberately exempt from the property denylist, so a $pageview or a
 * $pageleave fired there would ship the token to a third party. On the
 * control plane this component initializes nothing, captures no pageview and
 * no first-touch attribution; on a client transition INTO it, it opts the SDK
 * out immediately so the automatic $pageleave cannot carry a Sites URL.
 * Nothing about the visitor's stored consent record is touched, and normal
 * consent-gated behavior resumes on the way back out. /platform/app is
 * unaffected.
 */

function currentUrl(): string {
  return window.location.pathname + window.location.search
}

function PostHogSpaPageviews() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (typeof window === 'undefined' || !pathname) return
    if (isSitesControlPlanePath(pathname)) return
    if (!isAnalyticsAllowed()) return
    const search = searchParams?.toString()
    capturePostHogPageview(pathname + (search ? `?${search}` : ''))
    // First-touch UTM: consent-gated, first-touch-wins, no-op on untagged
    // navigation. Runs on every genuine navigation so a tagged landing that
    // is not the first route in the SPA session is still captured (once).
    captureFirstTouchUtm(searchParams ?? null)
  }, [pathname, searchParams])

  return null
}

export default function AnalyticsGate() {
  const onSitesControlPlane = isSitesControlPlanePath(usePathname())

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (onSitesControlPlane) {
      // No-capture boundary. On direct entry nothing has initialized, so this
      // is a no-op; on a transition in from marketing it opts the live SDK out
      // before the next automatic $pageleave can read a Sites URL. Deliberately
      // NOT applyDisallowedCleanup(): consent was not withdrawn here, and
      // dropping first-touch UTM / visitor id / AI referrer would destroy
      // legitimate marketing attribution just for visiting a workspace.
      disablePostHogCapture()
      return
    }

    // The cleanup applied whenever analytics are NOT currently allowed —
    // explicit withdrawal AND silent consent expiry. Expiry lapses the stored
    // record without firing consentDenied, so running this on every disallowed
    // page load makes stale-attribution cleanup independent of any later
    // contact-form read. Reuses the existing consent-change architecture; no
    // circular import (this component already owns first-touch-utm) and expiry
    // is not misrepresented as an explicit user denial.
    const applyDisallowedCleanup = () => {
      disablePostHogCapture()
      clearFirstTouchUtm()
      // Same lifecycle: withdrawing consent drops the anonymous LLM-referral
      // visitor id, so a later session cannot be joined to earlier attribution.
      clearVisitorId()
      // And the detected AI referrer. It moved to localStorage to survive
      // sessions, which also means it would survive a consent withdrawal and
      // keep riding along on contact submissions unless it is dropped here.
      clearStoredAIReferrer()
    }

    const startCapture = () => {
      // Capture the landing URL's first-touch UTM as soon as capture is
      // permitted — covers consent granted AFTER a tagged landing, where the
      // pageview effect above already ran (and skipped) pre-consent. Gated
      // again inside captureFirstTouchUtm; first-touch-wins keeps it idempotent.
      captureFirstTouchUtm(window.location.search)
      void enablePostHogCapture().then(() => {
        // Initial $pageview for this page load; deduped by URL so a
        // repeated consentGranted event can never double-count.
        capturePostHogPageview(currentUrl())
      })
    }

    if (isAnalyticsAllowed()) startCapture()
    else applyDisallowedCleanup()

    const onGranted = () => startCapture()
    const onDenied = () => applyDisallowedCleanup()
    window.addEventListener('consentGranted', onGranted)
    window.addEventListener('consentDenied', onDenied)
    return () => {
      window.removeEventListener('consentGranted', onGranted)
      window.removeEventListener('consentDenied', onDenied)
    }
  }, [onSitesControlPlane])

  return (
    <Suspense fallback={null}>
      <PostHogSpaPageviews />
    </Suspense>
  )
}
